interface SettingsSnapshot {
  sales_enabled?: boolean
  markup_percent?: number | null
}

// A provider without a settings row sells and inherits its markup.
const UNSET: SettingsSnapshot = { sales_enabled: true, markup_percent: null }

const asSnapshot = (value: unknown): SettingsSnapshot =>
  value && typeof value === 'object' ? (value as SettingsSnapshot) : UNSET

const markupText = (value: number | null | undefined, unset: string) =>
  value === null || value === undefined ? unset : `${value}%`

// History rows carry whole settings rows before and after the edit.
// `unset` names a null markup: inherited for a provider, default globally.
export function describeChange(
  before: unknown,
  after: unknown,
  unset = 'inherited',
): string[] {
  const was = asSnapshot(before)
  const now = asSnapshot(after)
  const changes: string[] = []
  if (
    was.sales_enabled !== now.sales_enabled &&
    now.sales_enabled !== undefined
  ) {
    changes.push(now.sales_enabled ? 'Sales enabled' : 'Sales disabled')
  }
  if ((was.markup_percent ?? null) !== (now.markup_percent ?? null)) {
    changes.push(
      `Markup ${markupText(was.markup_percent, unset)} → ${markupText(now.markup_percent, unset)}`,
    )
  }
  return changes
}

type ExclusionRow = Record<string, string | number> | null

const EXCLUSIONS: Record<
  string,
  { field: string; label: string; off: string; on: string }
> = {
  country_exclusion: {
    field: 'country_code',
    label: 'Country',
    off: 'excluded',
    on: 'returned to sale',
  },
  offer_exclusion: {
    field: 'provider_offer_id',
    label: 'Offer',
    off: 'taken off sale',
    on: 'returned to sale',
  },
  akash_provider_block: {
    field: 'owner',
    label: 'Provider',
    off: 'blocked',
    on: 'unblocked',
  },
}

// Exclusion history rows hold the exclusion row before and after; null on
// either side means it did not exist.
function describeExclusion(
  kind: (typeof EXCLUSIONS)[string],
  before: ExclusionRow,
  after: ExclusionRow,
) {
  const subject = `${kind.label} ${(after ?? before)?.[kind.field] ?? '?'}`
  if (!after) return `${subject} ${kind.on}`
  if (!before) return `${subject} ${kind.off}: ${after.reason}`
  return `${subject} reason changed: ${after.reason}`
}

export function describeEntry(
  entry: { change_type: string; before: unknown; after: unknown },
  unset: string,
): string[] {
  const kind = EXCLUSIONS[entry.change_type]
  if (kind) {
    return [
      describeExclusion(
        kind,
        entry.before as ExclusionRow,
        entry.after as ExclusionRow,
      ),
    ]
  }
  return describeChange(entry.before, entry.after, unset)
}
